import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import firebaseSync from '../firebaseSync';
import { isNetworkErrorEnvelope } from '../lib/offlineCache';
import * as offlineWriteQueue from '../lib/offlineWriteQueue';
import { remapListTempId, isResolvableTempId } from '../lib/offlineTempIdRemap';
import { animalAge } from '../lib/animalAge';

configure({ enforceActions: 'always' });

class SaleStore extends LocalModel {
    constructor() {
        super('sale', {
            namePlural: 'sales',
            sort: '-saleDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/sales/',
                    params: {
                        limit: 25,
                        sort: '-saleDate',
                    },
                },
                load: {
                    url: '/api/sales/',
                    params: {},
                },
                save: {
                    url: '/api/sales/',
                    params: {},
                },
                delete: {
                    url: '/api/sales/',
                    params: {},
                },
                changes: {
                    url: '/api/sales/changes',
                    params: {},
                },
            },
        });
    }

    reminderMethodIcons = {
        email: 'fa-solid fa-envelope',
        sms: 'fa-solid fa-sms',
        call: 'fa-solid fa-phone',
    };

    paymentMethodIcons = {
        cash: 'fa-solid fa-money-bill',
        card: 'fa-solid fa-credit-card',
        creditCard: 'fa-regular fa-credit-card',
        cardApp: 'fa-brands fa-apple-pay',
        cardExternal: 'fa-duotone fa-credit-card',
        invoice: 'fa-solid fa-file-invoice',
        account: 'fa-solid fa-file-invoice-dollar',
        vipps: 'fa-solid fa-mobile',
        app: 'fa-solid fa-mobile-screen-button',
        giftCard: 'fa-solid fa-gift',
        customerCredit: 'fa-solid fa-piggy-bank',
        paypal: 'fa-brands fa-paypal',
        convene: 'fa-solid fa-paper-plane',
        creditNote: 'fa-solid fa-dove',
        partial: 'fa-solid fa-exclamation',
        invoiceCredited: 'fa-solid fa-file-invoice',
        saleCredited: 'fa-solid fa-money-bill-transfer',
        priceOffer: 'fa-solid fa-handshake',
        archived: 'fa-solid fa-archive',
        collectiveInvoice: 'fa-solid fa-file-invoice-dollar',
        lost: 'fa-solid fa-times',
        subInvoice: 'fa-solid fa-file-invoice',
        roundoff: 'fa-solid fa-money-bill-wave',
        forbruk: 'fa-solid fa-basket-shopping',
        insurance: 'fa-solid fa-shield-heart',
        transferred: 'fa-solid fa-arrow-right-arrow-left',
    };

    paymentMethodIds = {
        cash: 100,
        card: 110,
        creditCard: 115,
        cardApp: 111,
        cardExternal: 112,
        invoice: 120,
        account: 130,
        vipps: 140,
        app: 141,
        giftCard: 145,
        customerCredit: 147,
        paypal: 146,
        creditNote: 150,
        partial: 160,
        invoiceCredited: 170,
        saleCredited: 180,
        priceOffer: 190,
        archived: 200,
        collectiveInvoice: 210,
        lost: 220,
        subInvoice: 230,
        roundoff: 240,
        forbruk: 250,
        convene: 260,
        insurance: 155,
        transferred: 156,
    };

    saleStatus = {
        inProgress: 0,
        paid: 1,
        unpaid: 2,
        partial: 3,
        invoice: 4,
        account: 5,
        creditNote: 6,
        invoiceCredited: 7,
        saleCredited: 8,
        conveneSent: 9,
        debtCollectionWarning: 10,
        debtCollection: 11,
        priceOffer: 1001,
        archived: 1002,
        collectiveInvoice: 1003,
        lost: 1004,
        subInvoice: 1005,
        forbruk: 1006,
        insuranceClaim: 1007,
        insuranceClaimSettled: 1008,
        employeePick: 9999,
    };

    saleStatusText = {
        inProgress: { en: 'In progress', no: 'Pågående' },
        paid: { en: 'Paid', no: 'Betalt' },
        unpaid: { en: 'Unpaid', no: 'Ubetalt' },
        partial: { en: 'Partial', no: 'Delvis' },
        invoice: { en: 'Invoice', no: 'Faktura' },
        account: { en: 'Account', no: 'Konto' },
        creditNote: { en: 'Credit note', no: 'Kreditnota' },
        invoiceCredited: { en: 'Invoice credited', no: 'Faktura kreditert' },
        saleCredited: { en: 'Sale credited', no: 'Salg kreditert' },
        conveneSent: { en: 'Sent to Convene', no: 'Sendt til Convene' },
        debtCollectionWarning: { en: 'Debt collection warning', no: 'Inkassovarsel' },
        debtCollection: { en: 'In debt collection', no: 'Til inkasso' },
        priceOffer: { en: 'Price offer', no: 'Pristilbud' },
        archived: { en: 'Archived', no: 'Arkivert' },
        collectiveInvoice: { en: 'Collective invoice', no: 'Samlefaktura' },
        lost: { en: 'Lost', no: 'Tap' },
        subInvoice: { en: 'Sub Invoice', no: 'Underbilag' },
        forbruk: { en: 'Internal use', no: 'Forbruk' },
        insuranceClaim: { en: 'Insurance claim sent', no: 'Krav sendt til forsikring' },
        insuranceClaimSettled: { en: 'Insurance claim settled', no: 'Krav behandlet' },
        employeePick: { en: 'Employee pick', no: 'Ansatt plukk' },
    };

    paymentMethodText = {
        cash: { en: 'Cash', no: 'Kontant' },
        card: { en: 'Card', no: 'Kort' },
        creditCard: { en: 'Credit Card', no: 'Kredittkort' },
        cardApp: { en: 'Card App', no: 'Kort App' },
        cardExternal: { en: 'Card (external)', no: 'Kort (ekstern)' },
        invoice: { en: 'Invoice', no: 'Faktura' },
        account: { en: 'Account', no: 'Konto' },
        vipps: { en: 'Vipps', no: 'Vipps' },
        app: { en: 'App', no: 'App' },
        giftCard: { en: 'Gift Card', no: 'Gavekort' },
        customerCredit: { en: 'Customer credit', no: 'Kundekonto' },
        paypal: { en: 'PayPal', no: 'PayPal' },
        convene: { en: 'Convene', no: 'Convene' },
        creditNote: { en: 'Credit note', no: 'Kreditnota' },
        partialCreditNote: { en: 'Partial credit note', no: 'Delvis kreditert' },
        partial: { en: 'Partial', no: 'Delvis' },
        invoiceCredited: { en: 'Invoice credited', no: 'Faktura kreditert' },
        saleCredited: { en: 'Sale credited', no: 'Salg kreditert' },
        priceOffer: { en: 'Price offer', no: 'Pristilbud' },
        archived: { en: 'Archived', no: 'Arkivert' },
        collectiveInvoice: { en: 'Collective invoice', no: 'Samlefaktura' },
        lost: { en: 'Lost', no: 'Tap' },
        subInvoice: { en: 'Sub Invoice', no: 'Underbilag' },
        roundoff: { en: 'Roundoff', no: 'Avrunding' },
        forbruk: { en: 'Internal use', no: 'Forbruk' },
        insurance: { en: 'Insurance', no: 'Forsikring' },
        transferred: { en: 'Transferred to invoice', no: 'Overført til egenandel' },
    }

    paymentMethodsActual = {
        cash: { en: 'Cash', no: 'Kontant' },
        card: { en: 'Card', no: 'Kort' },
        creditCard: { en: 'Credit Card', no: 'Kredittkort' },
        cardApp: { en: 'Card App', no: 'Kort App' },
        cardExternal: { en: 'Card (external)', no: 'Kort (ekstern)' },
        account: { en: 'Account', no: 'Konto' },
        vipps: { en: 'Vipps', no: 'Vipps' },
        app: { en: 'App', no: 'App' },
        invoice: { en: 'Invoice', no: 'Faktura' },
        paypal: { en: 'PayPal', no: 'PayPal' },
        convene: { en: 'Convene', no: 'Convene' },
    }

    paymentMethodsFilter = {
        cash: { en: 'Cash', no: 'Kontant' },
        card: { en: 'Card', no: 'Kort' },
        creditCard: { en: 'Credit Card', no: 'Kredittkort' },
        cardApp: { en: 'Card App', no: 'Kort App' },
        cardExternal: { en: 'Card (external)', no: 'Kort (ekstern)' },
        account: { en: 'Account', no: 'Konto' },
        vipps: { en: 'Vipps', no: 'Vipps' },
        app: { en: 'App', no: 'App' },
        invoice: { en: 'Invoice', no: 'Faktura' },
        paypal: { en: 'PayPal', no: 'PayPal' },
        convene: { en: 'Convene', no: 'Convene' },
        forbruk: { en: 'Internal use', no: 'Forbruk' },
        priceOffer: { en: 'Price offer', no: 'Pristilbud' },
    }

    saleStatusIcons = {
        inProgress: 'fa-solid fa-clock',
        paid: 'fa-solid fa-badge-check',
        unpaid: 'fa-solid fa-times',
        partial: 'fa-solid fa-exclamation',
        invoice: 'fa-solid fa-file-invoice',
        account: 'fa-solid fa-file-invoice-dollar',
        creditNote: 'fa-solid fa-dove',
        invoiceCredited: 'fa-solid fa-file-invoice',
        saleCredited: 'fa-solid fa-money-bill-transfer',
        conveneSent: 'fa-solid fa-paper-plane',
        debtCollectionWarning: 'fa-solid fa-triangle-exclamation',
        debtCollection: 'fa-solid fa-gavel',
        priceOffer: 'fa-solid fa-handshake',
        archived: 'fa-solid fa-archive',
        collectiveInvoice: 'fa-solid fa-file-invoice-dollar',
        lost: 'fa-solid fa-times',
        subInvoice: 'fa-solid fa-file-invoice',
        forbruk: 'fa-solid fa-basket-shopping',
        insuranceClaim: 'fa-solid fa-shield-heart',
        insuranceClaimSettled: 'fa-solid fa-shield-check',
        employeePick: 'fa-solid fa-user-gear',
    };

    saleStatusClass = {
        inProgress: 'secondary',
        paid: 'success',
        unpaid: 'warning',
        partial: 'danger',
        invoice: 'info',
        account: 'info',
        creditNote: 'info',
        invoiceCredited: 'info',
        saleCredited: 'info',
        conveneSent: 'info',
        debtCollectionWarning: 'warning',
        debtCollection: 'danger',
        priceOffer: 'primary',
        archived: 'secondary',
        collectiveInvoice: 'info',
        lost: 'danger',
        subInvoice: 'success',
        forbruk: 'secondary',
        insuranceClaim: 'warning',
        insuranceClaimSettled: 'success',
        employeePick: 'info',
    };

    // Insurance status - tracks invoices sent to insurance
    insuranceStatus = {
        none: 0,
        pending: 1,
        sent: 2,
        approved: 3,
        rejected: 4,
    };

    insuranceStatusText = {
        none: { en: 'Not applicable', no: 'Ikke aktuelt' },
        pending: { en: 'Pending', no: 'Venter' },
        sent: { en: 'Sent to insurance', no: 'Sendt til forsikring' },
        approved: { en: 'Approved', no: 'Godkjent' },
        rejected: { en: 'Rejected', no: 'Avvist' },
    };

    insuranceStatusIcons = {
        none: 'fa-solid fa-minus',
        pending: 'fa-solid fa-clock',
        sent: 'fa-solid fa-paper-plane',
        approved: 'fa-solid fa-check',
        rejected: 'fa-solid fa-times',
    };

    insuranceStatusClass = {
        none: 'secondary',
        pending: 'warning',
        sent: 'info',
        approved: 'success',
        rejected: 'danger',
    };

    checkForUpdatesCounter = 0;

    forms = [
        {
            id: 1,
            name: 'Innleggelse',
            description: 'Skjema til bruk ved innleggelse på klinikken.',
        },
        {
            id: 2,
            name: 'Urinprøve',
            description: 'Skjema til bruk ved urinprøver.',
        },

        {
            id: 101,
            logo: '/assets/logos/hjertedyr.webp',
            name: 'Hjertedyr',
            description: 'Drammen smådyrkrematorium AS.',
            address1: 'Drammen smådyrkrematorium AS',
            address2: 'Gartneriveien 5',
            postalcode: '3058',
            place: 'Solbergelva',
            phone: '923 50 077',
            email: 'info@hjertedyr.no',
            url: 'www.hjertedyr.no',
            orgNum: '923 888 934 MVA',
            info1: `__Viktig info til deg som nettopp har avlevert ditt Hjertedyr.__
Ønsker du ditt dyr tilbake i urne istedenfor enkel eske og dette ikke ble avklart hos veterinæren? Da kan du enkelt bestille denne på vår hjemmeside www.hjertedyr.no. Vi vil anbefale at dere gjør dette snarlig etter besøket hos din veterinær.
`,
            info2: `__Viktig info til veterinær.__
Denne rekvisisjonen skrives ut i 3 eksemplarer. 1 lagres i deres journalsystem, 1 gis til eier og ett eksemplar følger dyret. Gis til sjåfør ved henting av dyr.
`,
        },
        {
            id: 102,
            // Rendered with the crematorium's own 2.0 requisition layout, see
            // printForm.js (layout === 'skien2'). TICKET-01580.
            layout: 'skien2',
            logo: '/assets/logos/smadyrkrematoriet.jpg',
            name: 'Smådyr Krematoriet AS',
            description: 'Skien Smådyr Krematoriet AS',
            address1: 'Postboks 2504',
            postalcode: '3702',
            place: 'Skien',
            officeAddress1: 'Lyngbakkvegen 90',
            officePostalcode: '3736',
            officePlace: 'Skien',
            phone: '35594343',
            email: 'post@smaadyrkrematoriet.no',
            url: 'smådyrkrematoriet.no',
            orgNum: '977 330 750 MVA',
            info1: `__Viktig informasjon!__
Utfylt original legges i plastlomme og festes på sekken.
Rekvisisjonsnummer må være synlig i plastlommen.
Kunden beholder kopi av rekvisisjon.
`,
        },
        {
            id: 103,
            logo: '/assets/logos/bergen-smadyrkrematorium.png',
            name: 'Bergen Smådyrkrematorium AS',
            description: 'Bergen Smådyrkrematorium AS',
            address1: 'Håtuftvegen 405',
            // address2: 'Gartneriveien 5',
            postalcode: '5919',
            place: 'Frekhaug',
            phone: '91 71 17 74',
            email: 'post@bskrematorium.no',
            url: 'www.bskrematorium.no',
            orgNum: '999 100 686 MVA',
            info1: `__Viktig informasjon.__
Utfylt orginal rekvisisjon plasseres i plastlommen på plastsekk.
En kopi oppbevares av eier og en kopi oppbevares av veterinær.
`,
        },
        {
            id: 201,
            type: 'attest',
            name: 'Avlivningsattest',
            description: 'Attest for avlivning av dyr.',
        },
        {
            id: 202,
            type: 'attest',
            name: 'Helseattest',
            description: 'Helseerklæring for dyr.',
        },
        {
            id: 203,
            type: 'attest',
            name: 'Dødsattest',
            description: 'Bekreftelse på dødsfall.',
        },
        {
            id: 204,
            type: 'attest',
            name: 'Forsikringsattest',
            description: 'Veterinærattest til forsikringsselskap.',
        },
        {
            id: 205,
            type: 'attest',
            name: 'Kastreringsattest',
            description: 'Bekreftelse på kastrering.',
        },
        {
            id: 206,
            type: 'attest',
            name: 'Dekningsattest',
            description: 'Bekreftelse på bedekning og/eller drektighet.',
        },
    ];

    getPaymentMethodIcon(method) {
        return this.paymentMethodIcons[method] || '';
    }

    getReminderMethodIcon(method) {
        return this.reminderMethodIcons[method] || '';
    }

    getPaymentMethodText(method, languageText = 'no') {
        const text = this.paymentMethodText[method];
        if (text) {
            return text[languageText] || '';
        }
        return method;
    }

    getSaleStatusById(status, languageText) {
        // find status by id
        const statusId = parseInt(status, 10);
        const statusKey = Object.keys(this.saleStatus).find(key => this.saleStatus[key] === statusId);

        if (languageText) {
            const text = this.saleStatusText[statusKey];
            if (text) {
                return text[languageText] || '';
            }
        }

        return statusKey || '';
    }

    getSaleStatus(status) {
        // find status by key
        return this.saleStatus[status] || 0;
    }

    getSaleStatusIcon(status) {
        return this.saleStatusIcons[status] || '';
    }

    getSaleStatusClass(status) {
        return this.saleStatusClass[status] || '';
    }

    getInsuranceStatusById(status, languageText) {
        // find status by id
        const statusId = parseInt(status, 10);
        const statusKey = Object.keys(this.insuranceStatus).find(key => this.insuranceStatus[key] === statusId);

        if (languageText) {
            const text = this.insuranceStatusText[statusKey];
            if (text) {
                return text[languageText] || '';
            }
        }

        return statusKey || '';
    }

    getInsuranceStatus(status) {
        // find status by key
        return this.insuranceStatus[status] || 0;
    }

    getInsuranceStatusIcon(status) {
        return this.insuranceStatusIcons[status] || '';
    }

    getInsuranceStatusClass(status) {
        return this.insuranceStatusClass[status] || '';
    }

    hasPrescription(journals) {
        if (!journals || journals.length === 0) return false;
        for (let i = 0, l = journals.length; i < l; i += 1) {
            const journal = journals[i];
            if (journal && journal.prescriptions && journal.prescriptions.length > 0) {
                return journal.prescriptions[0];
            }
        }
    }

    getPrescription(journals) {
        if (!journals || journals.length === 0) return null;
        for (let i = 0, l = journals.length; i < l; i += 1) {
            const journal = journals[i];
            if (journal && journal.prescriptions && journal.prescriptions.length > 0) {
                return journal.prescriptions[0];
            }
        }
    }

    @observable newSale = {
        visitors: [],
        animals: [],
        journals: [],
        products: [],
        comments: [],
        selectedAnimals: [],
    };

    @observable sale = {};

    @observable saleDuplicate = {};

    @observable sales = [];

    @observable hasPendingSales = 0;

    @observable notUpdatedFields = [];

    @observable calendarNotification = {};

    @observable totalSales = 0;

    @observable searchResults = [];

    @observable veterinaries = [];

    @observable employees = [];

    // Clinic departments (customer.departments) for the sale-level department select.
    @observable departments = [];

    @observable visitorResults = [];

    @observable animalResults = [];

    @observable animalJournals = [];

    // Journals loaded specifically for printing an attest (dødsattest etc.) for a single
    // animal. Kept separate from animalJournals so opening the attest drawer never clobbers
    // the sale page's own journal state. See components/file/listTable.js.
    @observable attestJournals = [];

    @observable visitorAnimals = [];

    @observable visitorInvoices = [];

    @observable visitorSales = [];

    @observable writeoffs = [];
    @observable writeoffsSummary = {};
    @observable writeoffsLoading = false;

    @observable yearlyAccountsReport = null;
    @observable yearlyAccountsReportLoading = false;

    @observable accountsReceivableReport = null;
    @observable accountsReceivableReportLoading = false;

    @observable customerLedger = null;

    @observable customerLedgerLoading = false;

    @observable lastVisitors = [];

    // Remembers the last-used visit/sale date for the current session so the
    // next sale defaults to it (resets on reload). Helps when back-dating
    // several visits in a row, e.g. production-animal journals.
    @observable lastSaleDate = null;

    // Mirror of customer.featureFlags.saleDateTop. The store can't read the
    // customer, so the sale route sets this on load. Gates the prominent
    // top date bar behaviour (default + remember) below.
    @observable saleDateTopEnabled = false;

    // Mirror of customer.featureFlags.oreRoundingCashOnly. The store can't read
    // the customer, so the sale route sets this on load. When true: sale totals
    // keep exact øre (no round-to-krone); only cash rounds at payment time.
    @observable oreRoundingCashOnly = false;

    @observable users = [];

    @observable saleUsers = [];

    @observable customers = [];

    @observable currentVisitorSales = [];

    @observable currentSale = {};

    @observable currentPayments = [];

    @observable currentExternalPayments = [];

    @observable externalUserPayments = [];

    @observable allPayments = [];

    @observable visitorAllPayments = [];

    @observable productResults = [];

    @observable dailySales = [];

    @observable totalNumberOfSales = 0;

    @observable aggSaleTotal = {};

    @observable aggSaleTotalCollectiveInvoice = {};

    @observable aggSaleTotalDiscount = {};

    @observable aggSalesWithDiscount = [];

    @observable aggSaleTotalNew = {};

    @observable aggSaleTotalPaymentMethods = [];

    @observable aggSaleTotalPaymentMethodsNew = [];

    @observable aggSaleTotalTax = [];

    @observable aggSalePersons = [];

    @observable aggSalePersonsAccounts = [];

    @observable aggSalePaymentsByUser = [];

    @observable aggSaleTotalAccounts = [];

    @observable aggSaleTotalProducts = [];

    @observable reportUserDetails = [];

    @observable reportAllSales = [];

    @observable aggSaleWithJournals = {};

    @observable aggSaleNewVisitors = 0;

    @observable aggSaleActiveVisitors = 0;

    @observable journalCount = 0;
    @observable journalWithoutCount = 0;

    @observable saleTrends = [];

    @observable visitorTrends = [];

    @observable reportStatus = null;

    @observable cashflowAnalysis = null;
    @observable cashflowAnalysisLoading = false;

    @observable kpiDashboardData = {};
    @observable kpiDashboardLoading = false;

    @observable criticalTasksData = {};
    @observable criticalTasksLoading = false;

    // "Hva gjør jeg nå?" — AI plan for the critical tasks (POST /api/ai/critical-tasks-advice).
    @observable criticalTasksAdvice = {};
    @observable criticalTasksAdviceLoading = false;

    // "Ukens liste" — weekly checklist (GET /api/sales/report/weekly-checklist).
    @observable weeklyChecklist = {};
    @observable weeklyChecklistLoading = false;

    @observable cardBreakdown = null;
    @observable cardBreakdownLoading = false;

    @observable saleIsUpdated = false;

    @observable showProductComment = {};

    @observable showProductSaleUser = {};

    @observable showProductDepartment = {};

    // TICKET-01720: per-line saleDate editor (hospitalised patients, one line per day).
    @observable showProductSaleDate = {};

    @observable searchText = null;
    @observable showDeleted = null;
    @observable activeStatus = null;
    @observable activeInsuranceStatus = null;
    @observable activeUser = null;
    @observable activePaymentMethod = null;
    @observable activeVisitor = null;
    @observable currentPage = 1;
    @observable cutoffDate = null;
    @observable cutoffDateEnd = null;
    @observable formatting = true;
    @observable limit = 25;
    @observable sort = '-saleDate';

    @observable notTransferredToTripletex = null;
    @observable notTransferredToFiken = null;
    @observable notTransferredToDuett = null;
    @observable tripletexTransferredNotSent = null;
    @observable fikenTransferredNotSent = null;
    @observable duettTransferredNotSent = null;

    @observable criticalTask = null;
    @observable filterIds = null;

    @observable excludeSubInvoices = null;
    @observable onlySubInvoices = null;
    @observable onlyUnpaidWeborders = null;
    @observable defaultCutoffActive = false;

    // Cross-customer list toggle (featureFlags.sharedSales groups). null = off.
    @observable myCustomers = null;

    @observable reportDepartment = null;

    @observable cashCarryForward = null;

    @observable reportRequestVersion = 0;

    @action
    incrementReportRequestVersion() {
        this.reportRequestVersion += 1;
        return this.reportRequestVersion;
    }

    @computed get hasActiveFilters() {
        return !!(
            this.searchText ||
            (this.activeStatus !== null && this.activeStatus !== '') ||
            (this.activeInsuranceStatus !== null && this.activeInsuranceStatus !== '') ||
            (this.activePaymentMethod !== null && this.activePaymentMethod !== '') ||
            (this.activeUser !== null && this.activeUser !== '') ||
            (this.activeVisitor !== null && this.activeVisitor !== '') ||
            this.cutoffDate ||
            this.cutoffDateEnd ||
            this.showDeleted ||
            this.currentPage > 1 ||
            this.notTransferredToTripletex ||
            this.notTransferredToFiken ||
            this.notTransferredToDuett ||
            this.tripletexTransferredNotSent ||
            this.fikenTransferredNotSent ||
            this.duettTransferredNotSent ||
            this.excludeSubInvoices ||
            this.onlySubInvoices ||
            this.onlyUnpaidWeborders ||
            this.criticalTask ||
            (Array.isArray(this.filterIds) && this.filterIds.length > 0)
        );
    }

    @action
    clearAllFilters() {
        this.searchText = '';
        this.activeStatus = '';
        this.activeInsuranceStatus = '';
        this.activePaymentMethod = '';
        this.activeUser = '';
        this.activeVisitor = '';
        this.cutoffDate = '';
        this.cutoffDateEnd = '';
        this.showDeleted = false;
        this.currentPage = 1;
        this.notTransferredToTripletex = false;
        this.notTransferredToFiken = false;
        this.notTransferredToDuett = false;
        this.tripletexTransferredNotSent = false;
        this.fikenTransferredNotSent = false;
        this.duettTransferredNotSent = false;
        this.excludeSubInvoices = false;
        this.onlySubInvoices = false;
        this.onlyUnpaidWeborders = false;
        this.criticalTask = null;
        this.filterIds = null;
        this.myCustomers = null;
    }

    // Preset to previous month  1.
    @observable collectiveInvoiceCutoffDate = util.isoDate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), false, false, true);
    @observable collectiveInvoiceCutoffDateEnd = util.isoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 0), false, false, true);

    @observable isProductionAnimals = false;

    // Save feedback for success/error notifications
    @observable saveFeedback = null;

    @action
    setSaveFeedback(status, message = '', field = '') {
        this.saveFeedback = {
            status, // 'success' | 'error'
            message,
            field,
            timestamp: Date.now(),
        };
    }

    @action
    replaceSaleId(tempId, realId, response) {
        if (!realId) return;
        const sales = this.sales || [];
        const idx = sales.findIndex(s => s && s.id === tempId);
        if (idx >= 0) {
            const next = sales.slice();
            const updated = { ...next[idx], id: realId, _pendingSync: false, _syncFailed: false };
            const sale = response && response.data && response.data.sale;
            if (sale) {
                if (sale.saleNumber != null) updated.saleNumber = sale.saleNumber;
                if (sale.updatedDate != null) updated.updatedDate = sale.updatedDate;
                if (sale.saleDate != null) updated.saleDate = sale.saleDate;
            }
            next[idx] = updated;
            this.updateKeyValue('sales', next);
        }
        if (this.newSale && this.newSale.id === tempId) {
            this.updateObjectKeyValue('newSale', 'id', realId);
            this.updateObjectKeyValue('newSale', '_pendingSync', false);
        }
    }

    /**
     * An offline record just synced and got its real id — swap the temp id in
     * the sale the user still has open, so the NEXT save (which may well go
     * straight to the server with no drain involved) carries the real id.
     * Without this the temp id is persisted permanently: the sale ends up with
     * an owner that cannot be resolved, and the accounting transfer has no
     * customer to book on.
     */
    @action
    replaceOfflineTempId(tempId, realId) {
        if (!isResolvableTempId(tempId, realId)) return;
        const { newSale } = this;
        if (newSale) {
            const visitors = remapListTempId(newSale.visitors, ['id'], tempId, realId);
            if (visitors !== newSale.visitors) {
                this.updateObjectKeyValue('newSale', 'visitors', visitors);
            }
            const animals = remapListTempId(newSale.animals, ['id', 'owner'], tempId, realId);
            if (animals !== newSale.animals) {
                this.updateObjectKeyValue('newSale', 'animals', animals);
            }
            const journals = remapListTempId(newSale.journals, ['id'], tempId, realId);
            if (journals !== newSale.journals) {
                this.updateObjectKeyValue('newSale', 'journals', journals);
            }
        }
        const visitorAnimals = remapListTempId(this.visitorAnimals, ['id', 'owner'], tempId, realId);
        if (visitorAnimals !== this.visitorAnimals) {
            this.updateKeyValue('visitorAnimals', visitorAnimals);
        }
    }

    @action
    markSaleSyncFailed(tempId, lastError) {
        const sales = this.sales || [];
        const idx = sales.findIndex(s => s && s.id === tempId);
        if (idx >= 0) {
            const next = sales.slice();
            next[idx] = { ...next[idx], _pendingSync: false, _syncFailed: true, _syncError: lastError || null };
            this.updateKeyValue('sales', next);
        }
    }

    @action
    _setHasPendingSales(count) {
        this.updateKeyValue('hasPendingSales', count || 0);
    }

    /**
     * Shared loader for every "previous sales for this visitor" mini-list.
     * opt.query carries the call site's extra keys ({ today: 1 } or
     * { status: 0 }); opt.myCustomers widens the scope to the whole
     * data-sharing group (featureFlags.sharedSales) — see
     * lib/crossCustomerSales.js.
     */
    async loadVisitorSales(visitorIds, opt = {}) {
        if (!util.isArray(visitorIds) || visitorIds.length === 0) {
            return false;
        }
        const response = await this.load({
            query: {
                visitor: visitorIds,
                ...(opt.query || {}),
                myCustomers: opt.myCustomers ? 1 : undefined,
            },
            skipUpdate: true,
        });
        this.updateKeyValue('currentVisitorSales', response || []);
        return response;
    }

    @action
    async loadPendingSales() {
        let entries = [];
        try {
            entries = await offlineWriteQueue.getAll();
        } catch (e) {
            entries = [];
        }
        if (!Array.isArray(entries) || entries.length === 0) {
            this._setHasPendingSales(0);
            return;
        }
        // Feed posts share the queue but are not sales — never surface them here.
        entries = entries.filter(entry => entry && entry.type !== 'article');
        if (entries.length === 0) {
            this._setHasPendingSales(0);
            return;
        }
        const existingIds = new Set((this.sales || []).map(s => s && s.id));
        const merged = (this.sales || []).slice();
        for (const entry of entries) {
            if (existingIds.has(entry.tempId)) continue;
            const display = {
                ...entry.saleData,
                id: entry.tempId,
                _pendingSync: entry.status === 'pending',
                _syncFailed: entry.status === 'failed',
                _syncError: entry.lastError || null,
            };
            merged.unshift(display);
        }
        this.updateKeyValue('sales', merged);
        this._setHasPendingSales(entries.length);
    }

    @action
    clearSaveFeedback() {
        this.saveFeedback = null;
    }

    @action
    clearAll() {
        this.newSale = {
            visitors: [],
            animals: [],
            journals: [],
            products: [],
            selectedAnimals: [],
        };
        this.sale = {};
        this.sales = [];
        this.visitorResults = [];
        this.animalResults = [];
        this.animalJournals = [];
        this.visitorAnimals = [];
        this.visitorSales = [];
        this.lastVisitors = [];
        this.currentVisitorSales = [];
        this.currentSale = {};
        this.currentPayments = [];
        this.productResults = [];
        this.dailySales = [];
        this.saveFeedback = null;
    }

    // Target animals for new product lines.
    // `selectedAnimals` is the authoritative multi-select list (one product line is
    // created per id when a product is added). `selectedAnimal` is kept in sync as the
    // single "primary" (last ticked) so copy mode, the picker species filter and the
    // picker prepend keep working unchanged. Always replace observable arrays with a
    // fresh array (MobX strict mode).
    @action
    toggleSelectedTargetAnimal(animalId) {
        const id = parseInt(animalId, 10);
        if (!(id > 0)) {
            return;
        }
        const current = this.newSale.selectedAnimals || [];
        const isSelected = current.includes(id);
        const next = isSelected ? current.filter(x => x !== id) : [...current, id];
        this.updateObjectKeyValue('newSale', 'selectedAnimals', next);
        if (isSelected) {
            // Removed; if we removed the current primary, fall back to the last remaining.
            if (this.newSale.selectedAnimal === id) {
                this.updateObjectKeyValue('newSale', 'selectedAnimal', next.length ? next[next.length - 1] : null);
            }
        } else {
            this.updateObjectKeyValue('newSale', 'selectedAnimal', id);
        }
    }

    // Pure replace: make `id` the only target (resets the multi-select). Falsy id clears.
    // Callers that want toggle-off semantics (e.g. the mini animal list) do their own
    // equality check before calling.
    @action
    setSelectedTargetAnimal(animalId) {
        const id = parseInt(animalId, 10);
        if (!(id > 0)) {
            this.updateObjectKeyValue('newSale', 'selectedAnimals', []);
            this.updateObjectKeyValue('newSale', 'selectedAnimal', null);
            return;
        }
        this.updateObjectKeyValue('newSale', 'selectedAnimals', [id]);
        this.updateObjectKeyValue('newSale', 'selectedAnimal', id);
    }

    @action
    setSelectedTargetAnimals(animalIds = []) {
        const ids = (animalIds || []).map(x => parseInt(x, 10)).filter(x => x > 0);
        this.updateObjectKeyValue('newSale', 'selectedAnimals', ids);
        this.updateObjectKeyValue('newSale', 'selectedAnimal', ids.length ? ids[ids.length - 1] : null);
    }

    @action
    toggleProductComment(md5) {
        this.showProductComment[md5] = !this.showProductComment[md5];
    }

    @action
    toggleProductSaleUser(md5) {
        this.showProductSaleUser[md5] = !this.showProductSaleUser[md5];
    }

    @action
    toggleProductDepartment(md5) {
        this.showProductDepartment[md5] = !this.showProductDepartment[md5];
    }

    @action
    toggleProductSaleDate(md5) {
        this.showProductSaleDate[md5] = !this.showProductSaleDate[md5];
    }

    @action
    setSortField(field) {
        this.sort = field;
    }

    linePartPrice({ line, field = 'price', format = true, inputQty, exclVat = false }) {
        const qty = inputQty ?? (line.qty != null ? line.qty : 1);
        if (!qty) return 0;

        let aPrice = line[field];
        if (!aPrice) {
            return 0;
        }
        let price = qty * (aPrice || 0);
        if (line.discount) {
            price -= price * (line.discount / 100);
        }
        const taxAmount = exclVat ? 0 : price * ((line.vat || 0) / 100);
        let totalPrice = price + taxAmount;
        // if (line.discountAmount) {
        //     totalPrice -= (line.discountAmount * qty);
        // }
        if (format) {
            return util.format(totalPrice, 2, ',', ' ');
        }
        return totalPrice;
    }

    linePricePart(line, format = true, part = 'medicine', exclVat = false) {
        let aPrice = line[part] || 0;
        const taxAmount = exclVat ? 0 : aPrice * ((line.vat || 0) / 100);
        let totalPrice = aPrice + taxAmount;
        if (format) {
            return util.format(totalPrice, 2, ',', ' ');
        }
        return totalPrice;
    }

    lineTotal(line, format = true, inputQty, exclVat = false) {
        const qty = inputQty ?? (line.qty != null ? line.qty : 1);
        if (!qty) return 0;

        let aPrice = line.price ?? 0;
        if (line.medicine || line.fee || line.equipment || line.other) {
            aPrice = parseFloat(line.medicine || 0) + parseFloat(line.fee || 0) + parseFloat(line.equipment || 0) + parseFloat(line.other || 0);
        }
        let price = qty * (aPrice || 0);
        if (line.discount) {
            price -= price * (line.discount / 100);
        }
        const taxAmount = exclVat ? 0 : price * ((line.vat || 0) / 100);
        let totalPrice = price + taxAmount;
        if (line.discountAmount) {
            // discountAmount is VAT-inclusive; in exclVat mode, remove VAT from discount
            const discAmt = exclVat ? (line.discountAmount / (1 + (line.vat || 0) / 100)) : line.discountAmount;
            totalPrice -= ((discAmt || 0) * qty);
        }
        if (format) {
            return util.format(totalPrice, 2, ',', ' ');
        }
        return totalPrice;
    }

    lineDiscount(line, format = true, inputQty) {
        const qty = line.qty || inputQty || 1;
        let totalDiscount = qty * (line.discountAmount || 0);
        if (format) {
            return util.format(totalDiscount, 2, ',', ' ');
        }
        return totalDiscount;
    }

    /**
     * Splits the sale total into its price segments (medisin/honorar/utstyr/annet)
     * so an invoice can print a breakdown that adds up to the total.
     *
     * TICKET-01405: accountants asked for a summeringslinje per segment at the
     * bottom of a samlefaktura. The invoice already prints the segment columns
     * per line, but those are UNIT prices (linePricePart ignores qty), so they
     * cannot be added up by hand.
     *
     * The split mirrors lineTotal() exactly: qty, percentage discount and VAT all
     * scale linearly, and the VAT-inclusive discountAmount is apportioned across
     * the segments by their share of the line price. Lines with no segment prices
     * at all (fakturagebyr, frakt, plain products) are bucketed into `other`, so
     * the parts always reconcile with sumTotal().
     *
     * Returns raw numbers — the caller formats them.
     */
    sumTotalParts(lines, exclVat = false) {
        const partNames = ['medicine', 'fee', 'equipment', 'other'];
        const parts = { medicine: 0, fee: 0, equipment: 0, other: 0 };
        if (!lines) return parts;

        lines.forEach(line => {
            const qty = line.qty != null ? line.qty : 1;
            if (!qty) return;
            const vat = exclVat ? 0 : (line.vat || 0);

            // Same truthiness check as lineTotal(): any segment field set means the
            // segments — not line.price — define the line price. Segments can be
            // persisted as strings (TICKET-01381), hence parseFloat throughout.
            const hasSegments = line.medicine || line.fee || line.equipment || line.other;
            const segments = { medicine: 0, fee: 0, equipment: 0, other: 0 };
            let segmentSum = 0;
            if (hasSegments) {
                partNames.forEach(part => {
                    segments[part] = parseFloat(line[part] || 0) || 0;
                    segmentSum += segments[part];
                });
            } else {
                segments.other = parseFloat(line.price ?? 0) || 0;
                segmentSum = segments.other;
            }
            // Nothing to distribute — matches lineTotal(), which totals 0 for a line
            // whose segments are all set but sum to zero.
            if (!segmentSum) return;

            // discountAmount is VAT-inclusive, so strip VAT from it in exclVat mode.
            const discountAmount = line.discountAmount
                ? (exclVat ? line.discountAmount / (1 + ((line.vat || 0) / 100)) : line.discountAmount)
                : 0;

            partNames.forEach(part => {
                const segment = segments[part];
                if (!segment) return;
                let partPrice = qty * segment;
                if (line.discount) {
                    partPrice -= partPrice * (line.discount / 100);
                }
                partPrice += partPrice * (vat / 100);
                partPrice -= discountAmount * qty * (segment / segmentSum);
                parts[part] += partPrice;
            });
        });

        // Round to whole krone (legacy) or keep exact øre, same as sumTotal().
        const round = value => (this.oreRoundingCashOnly
            ? Math.round((value + Number.EPSILON) * 100) / 100
            : Math.round(value));
        const total = round(partNames.reduce((sum, part) => sum + parts[part], 0));
        partNames.forEach(part => {
            parts[part] = round(parts[part]);
        });

        // Rounding each part on its own can drift a krone away from the rounded
        // total. Put the residual on the biggest part so the printed breakdown
        // always adds up to the Total line on the invoice.
        const residual = total - partNames.reduce((sum, part) => sum + parts[part], 0);
        if (residual) {
            const biggest = partNames.reduce((best, part) => (
                Math.abs(parts[part]) > Math.abs(parts[best]) ? part : best
            ), partNames[0]);
            parts[biggest] = round(parts[biggest] + residual);
        }

        return parts;
    }

    sumTotal(lines, format = true, exclVat = false) {
        if (!lines) return 0;
        let total = 0;
        lines.forEach(line => {
            total += this.lineTotal(line, false, undefined, exclVat);
        });
        // Round to whole krone (legacy) or keep exact øre (cash-only øreavrunding flag on).
        total = this.oreRoundingCashOnly ? Math.round((total + Number.EPSILON) * 100) / 100 : Math.round(total);

        if (format) {
            return util.format(total, 2, ',', ' ');
        }
        return total;
    }

    sumTotalDiscount(lines, format = true) {
        if (!lines) return 0;
        let totalDiscount = 0;
        lines.forEach(line => {
            totalDiscount += this.lineDiscount(line, false);
        });
        // Round to whole krone (legacy) or keep exact øre (cash-only øreavrunding flag on).
        totalDiscount = this.oreRoundingCashOnly ? Math.round((totalDiscount + Number.EPSILON) * 100) / 100 : Math.round(totalDiscount);

        if (format) {
            return util.format(totalDiscount, 2, ',', ' ');
        }
        return totalDiscount;
    }

    sumTotalAll(sales, format = true) {
        if (!sales) return 0;
        let total = 0;
        sales.forEach(sale => {
            const tot = this.sumTotal(sale.products, false);
// console.log('sumTotalAll.tot', tot);
            total += tot;
        });
        if (format) {
            return util.format(total, 2, ',', ' ');
        }
        return total;
    }

    sumTotalAllDiscount(sales, format = true) {
        if (!sales) return 0;
        let total = 0;
        sales.forEach(sale => {
            const tot = this.sumTotalDiscount(sale.products, false);
// console.log('sumTotalAll.tot', tot);
            total += tot;
        });
        if (format) {
            return util.format(total, 2, ',', ' ');
        }
        return total;
    }

    sumRest(sale, format = true) {
        if (!sale || !sale.partialPayments) return 0;

        const lines = sale.products || [];
        let total = 0;
        lines.forEach(line => {
            total += this.lineTotal(line, false);
        });
        sale.partialPayments.forEach(payment => {
            total -= payment.amount;
        });

        // Round to whole krone (legacy) or keep exact øre (cash-only øreavrunding flag on).
        total = this.oreRoundingCashOnly ? Math.round((total + Number.EPSILON) * 100) / 100 : Math.round(total);

        if (format) {
            return util.format(total, 2, ',', ' ');
        }
        return total;
    }

    sumTotalPayments(paymentList, format = true) {
        if (!paymentList || paymentList.length === 0) return 0;
        let totalPaid = 0;
        paymentList.forEach(payment => {
            totalPaid += payment.amount;
        });

        if (format) {
            return util.format(totalPaid, 2, ',', ' ');
        }
        return totalPaid;
    }

    sumRestPayments(paymentList, total, format = true, hideEmptyOutput = false) {
        if (!paymentList || paymentList.length === 0) return total;
        let totalPaid = 0;
        paymentList.forEach(payment => {
            // Don't re-round each payment when keeping exact øre — exact non-cash payments
            // (e.g. 1234.50) must not be rounded up in the "rest to pay" math.
            totalPaid += this.oreRoundingCashOnly ? payment.amount : Math.round(payment.amount);
        });

        if (hideEmptyOutput && total - totalPaid === 0) {
            return '';
        }
        if (format) {
            return util.format(total -totalPaid, 2, ',', ' ');
        }
// console.log('totalPaid', totalPaid);
// console.log('total', total);
// console.log('total - totalPaid', total - totalPaid);
        return total - totalPaid;
    }

    sumRestAll(sales, format = true) {
        if (!sales) return 0;
        let total = 0;
        sales.filter(s => s.status === 0 || s.status === 2 || s.status === 3).forEach(sale => {
            const rest = this.sumRest(sale, false);
            if (rest > 0) {
                total += rest;
            } else {
                const tot = this.sumTotal(sale.products, false);
                if (tot > 0) {
                    total += tot;
                }
            }
        });
        if (format) {
            return util.format(total, 2, ',', ' ');
        }
        return total;
    }

    sumVat(lines) {
        if (!lines) return 0;
        let totalTax = 0;
        lines.forEach(product => {
            const qty = product.qty || 1;
            let aPrice = product.price ?? 0;
            if (product.medicine || product.fee || product.equipment || product.other) {
                aPrice = parseFloat(product.medicine || 0) + parseFloat(product.fee || 0) + parseFloat(product.equipment || 0) + parseFloat(product.other || 0);
            }
            let productPrice = qty * aPrice;
            if (product.discount) {
                productPrice -= productPrice * (product.discount / 100);
            }
            if (product.discountAmount > 0) {
                productPrice -= ((product.discountAmount || 0) * qty);
            }
            const taxAmount = productPrice * ((product.vat || 0) / 100);
            totalTax += taxAmount;
        });
        return util.format(totalTax, 2, ',', ' ');
    }

    @computed
    get saleTotal() {
        if (!this.newSale || !this.newSale.products) return 0;
        let total = 0;
        this.newSale.products.forEach(product => {
            const qty = product.qty || 1;
            let aPrice = product.price ?? 0;
            if (product.medicine || product.fee || product.equipment || product.other) {
                aPrice = parseFloat(product.medicine || 0) + parseFloat(product.fee || 0) + parseFloat(product.equipment || 0) + parseFloat(product.other || 0);
            }
            let productPrice = qty * aPrice;
            if (product.discount) {
                productPrice -= productPrice * (product.discount / 100);
            }
            const taxAmount = productPrice * ((product.vat || 0) / 100);
            let totalPrice = productPrice + taxAmount;
            if (product.discountAmount > 0) {
                totalPrice -= ((product.discountAmount || 0) * qty);
            }
            total += totalPrice;
            // substract discount for each line. Ie 40% discount on product price.
        });

        // Round to whole krone (legacy) or keep exact øre (cash-only øreavrunding flag on).
        total = this.oreRoundingCashOnly ? Math.round((total + Number.EPSILON) * 100) / 100 : Math.round(total);
        return total;
    }

    @computed
    get saleTax() {
        // Calculate tax part on all products
        if (!this.newSale || !this.newSale.products) return 0;
        let totalTax = 0;
        this.newSale.products.forEach(product => {
            const qty = product.qty || 1;
            let aPrice = product.price ?? 0;
            if (product.medicine || product.fee || product.equipment || product.other) {
                aPrice = parseFloat(product.medicine || 0) + parseFloat(product.fee || 0) + parseFloat(product.equipment || 0) + parseFloat(product.other || 0);
            }
            let productPrice = qty * aPrice;
            if (product.discount) {
                productPrice -= productPrice * (product.discount / 100);
            }
            let taxAmount = productPrice * ((product.vat || 0) / 100);
            // console.log('taxAmount', taxAmount);
            if (product.discountAmount) {
                // Discount has tax included and we need to get the tax part of the discount
                const discountTax = (product.discountAmount * qty) / 1.25 * 0.25;
                // console.log('discountTax', discountTax);
                taxAmount -= discountTax;
            }
            // console.log('taxAmount for line', taxAmount);
// console.log('product.discountAmount', product.discountAmount);
// console.log('taxAmount', taxAmount);
            totalTax += taxAmount;
        });
        return util.format(totalTax, 2, ',', ' ');
    }

    calculateTax(totalWithoutTax, vat = 25) {
        const taxAmount = totalWithoutTax * (vat / 100);
        return taxAmount;
    }

    makeNewSaleTitle() {
        // Make title for sale
        const { visitors, animals = [], saleDate = new Date() } = this.newSale;
        let newSaleTitle = `Sale ${util.formatDate(saleDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}`;
        if (visitors && visitors.length >= 0 && visitors[0]) {
            const visitor = visitors[0];
            newSaleTitle = visitor.firstname ? `${visitor.firstname} ${visitor.lastname}` : `${visitor.name}`;
        }
        if (animals && animals.length >= 0 && animals[0]) {
            const animal = animals[0];
            newSaleTitle = `${newSaleTitle} - ${animal.name} ${animal.birth ? `(${animalAge(animal.birth)})` : ''}`;
        }
        this.updateObjectKeyValue('newSale', 'title', newSaleTitle);
    }

    prepareSale(props = {}) {
        const { newSale } = this;
        const { visitors, animals, journals, products, productAdded, productRemoved } = newSale;
        let sale = {
            id: newSale.id,
        };
        if (props.fieldsToSave) {
            props.fieldsToSave.forEach(field => {
                if (['visitors', 'animals', 'journals', 'products'].indexOf(field) > -1) {
                    sale[`${field}-overwrite`] = newSale[field];
                } else {
                    sale[field] = newSale[field];
                }
            });
        } else {
            sale = {
                ...newSale,
                ['visitors-overwrite']: visitors,
                ['animals-overwrite']: animals,
                ['journals-overwrite']: journals,
                ['products-overwrite']: products,
            };
        }
        if (props.calledFrom) {
            sale.calledFrom = props.calledFrom;
        }
        // Deliberate unpay/reopen flows (e.g. "Sett til pågående") must opt in to
        // bypass the server-side revert-guard that otherwise drops status→0 /
        // cleared payment fields on a sale that already has a salePayment.
        if (props.forceUnpay) {
            sale.forceUnpay = true;
        }
        if (props.event) {
            sale.event = {
                isTrusted: props.event?.isTrusted,
                clientX: props.event?.clientX,
                clientY: props.event?.clientY,
                pageX: props.event?.pageX,
                pageY: props.event?.pageY,
                altKey: props.event?.altKey,
                ctrlKey: props.event?.ctrlKey,
            };
        }
        // if (window) {
        //     sale.window = {
        //         screenWidth: window.screen.width,
        //         screenHeight: window.screen.height,
        //         innerWidth: window.innerWidth,
        //         innerHeight: window.innerHeight,
        //     };
        // }
        delete sale.visitors;
        delete sale.animals;
        delete sale.journals;
        delete sale.products;
        delete sale.saleDate;
        delete sale.saleNumber;

        // Only guard FULL saves against stale-context product clobbering. When the
        // caller explicitly asked to save products (fieldsToSave includes 'products'),
        // it just mutated them — always send the payload. Gating explicit saves on
        // productAdded/productRemoved silently stripped the products whenever an
        // earlier in-flight save completed (and reset the flags) between a keystroke
        // and its debounced save, so typed line amounts never reached the server
        // (TICKET-00980).
        const explicitProducts = Array.isArray(props.fieldsToSave) && props.fieldsToSave.indexOf('products') > -1;
        if (!explicitProducts && !productAdded && !productRemoved) {
            // If no products added or removed, then use the existing products
            delete sale['products-overwrite'];
        }

        // fieldsToSkip: ['paymentMethod', 'paymentAmount', 'status', 'paidDate']
        if (props.fieldsToSkip) {
            props.fieldsToSkip.forEach(field => {
                delete sale[field];
            });
        }

        return sale;
    }

    // getVeterinary(id) {
    //     return this.veterinaries.find(vet => vet.id === id);
    // }

    getEmployee(id) {
        return this.employees.find(emp => emp.id === id);
    }

    // async loadVeterinaries() {
    //     const response = await util.fetchApi(`/api/users/veterinaries/`, { method: 'GET' }, {});
    //     switch (response.status) {
    //         case 200:
    //             this.updateKeyValue('veterinaries', response.data);
    //             return response;

    //     }
    // }

    async loadEmployees() {
        const response = await util.fetchApi(`/api/users/employees/`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('employees', response.data);
                return response;
        }
    }

    /**
     * Saves a sale.
     *
     * @param {Object} props - The properties for the sale.
     * @param {Array} props.visitors - The list of visitors for the sale.
     * @param {Array} props.animals - The list of animals for the sale.
     * @param {Array} props.journals - The list of journals for the sale.
     * @param {Array} props.products - The list of products for the sale.
     * @param {string} props.calledFrom - The source of the sale.
     * @return {boolean} Returns true if the sale is saved successfully, otherwise false.
     */
    async saveSale(props = {}) {
        const { newSale } = this;
        const hasVisitors = newSale.visitors && newSale.visitors.length > 0;
        const hasAnimals = newSale.animals && newSale.animals.length > 0;
        const hasJournal = newSale.journals && newSale.journals.length > 0;
        const hasProducts = newSale.products && newSale.products.length > 0;

        if (!hasVisitors) {
            // A persisted sale with no owner is a broken client state (a failed
            // visitor reload used to blank the list). Say so instead of dropping
            // the save silently; a brand-new empty sale is still skipped quietly
            // (TICKET-01643).
            if (newSale.id > 0) {
                this.setSaveFeedback('error', 'Not saved: the sale has no owner. Reload the page and open the sale again.');
            }
            return false; // Do NOT save empty sales
        }
        if (!(hasProducts || hasJournal || (hasVisitors && hasAnimals))) {
            return false; // Do NOT save empty sales
        }

        // TODO: Need to add latest version of journal to sale.
        if (newSale.id && newSale.id < 0) {
            // tempId — sale lives only in the offline write queue. Refresh
            // the queued POST body with the current newSale state so the
            // eventual drain submits the merged final state. Never hit the
            // network here — the sale doesn't exist server-side yet.
            const refreshedBody = {
                ...newSale,
                user: newSale.user || null,
                isProductionAnimal: newSale.isProductionAnimal,
                calledFrom: props.calledFrom,
            };
            delete refreshedBody.id;
            delete refreshedBody._pendingSync;
            // Flags consumed by the body snapshot (TICKET-00980).
            this.updateObjectKeyValue('newSale', 'productAdded', false);
            this.updateObjectKeyValue('newSale', 'productRemoved', false);
            const updated = await offlineWriteQueue.mutateInsert(newSale.id, () => refreshedBody);
            if (!updated) {
                this.setSaveFeedback('error', 'Pending sale not found in offline queue.');
                return false;
            }
            // Reflect the refreshed body back onto the in-store sale entry
            // so any list view shows current data.
            const sales = (this.sales || []).slice();
            const idx = sales.findIndex(s => s && s.id === newSale.id);
            if (idx >= 0) {
                sales[idx] = { ...sales[idx], ...refreshedBody, id: newSale.id, _pendingSync: true };
                this.updateKeyValue('sales', sales);
            }
            this.setSaveFeedback('success', 'Sale saved (will sync when online)');
        } else if (newSale.id) {
            // this.makeNewSaleTitle();
            const preparedSale = this.prepareSale(props);
            // The flags are consumed by the snapshot above. Clear them BEFORE the
            // network round-trip: clearing after `await` wiped flags set by edits
            // made while this save was in flight, so the next debounced save went
            // out without its products payload (TICKET-00980).
            const hadProductAdded = !!newSale.productAdded;
            const hadProductRemoved = !!newSale.productRemoved;
            this.updateObjectKeyValue('newSale', 'productAdded', false);
            this.updateObjectKeyValue('newSale', 'productRemoved', false);
            const response = await this.save(preparedSale);

            // Check for error response
            if (response?.status >= 400) {
                // Restore the consumed flags so a retry still carries the products.
                if (hadProductAdded) this.updateObjectKeyValue('newSale', 'productAdded', true);
                if (hadProductRemoved) this.updateObjectKeyValue('newSale', 'productRemoved', true);
                this.setSaveFeedback('error', response?.message || response?.error || 'Failed to save sale', response?.field);
                return false;
            }

            const sale = response?.data?.sale;
            // console.log(sale)
            this.updateObjectKeyValue('newSale', 'updatedDate', sale?.updatedDate);
            this.updateObjectKeyValue('newSale', 'saleNumber', sale?.saleNumber);
            // this.updateObjectKeyValue('newSale', 'paidDate', sale?.paidDate);
            // this.updateObjectKeyValue('newSale', 'products', sale?.products);
            this.updateKeyValue('notUpdatedFields', response?.included?.notUpdatedFields);
            this.updateKeyValue('calendarNotification', response?.included?.calendarNotification);
            this.setSaveFeedback('success', 'Sale saved');
        } else {
            // Double-submit guard (TICKET-01118): a brand-new sale (no id yet)
            // must only ever be inserted ONCE. Two near-simultaneous callers
            // (e.g. a debounced auto-save racing the pay/invoice action) both
            // saw newSale.id === undefined and each POSTed, creating a duplicate
            // besøk ~21ms apart. If an insert for this new sale is already in
            // flight, wait for it — by then newSale.id is set — then fall through
            // as an update so this caller's own fields still get saved.
            if (this._newSaleInsertPromise) {
                await this._newSaleInsertPromise;
                return this.saveSale(props);
            }
            let releaseInsertGuard;
            this._newSaleInsertPromise = new Promise((resolve) => { releaseInsertGuard = resolve; });
            try {
                // Remember the visit/sale date for the rest of the session so the
                // next sale defaults to it (helps when back-dating several visits
                // in a row). Only a brand-new sale counts — saving a LOADED old
                // sale must not poison the remembered date with that sale's
                // historic timestamp — and only a deliberate backdate (another
                // calendar day) sticks. Same-day dates clear the memory so a tab
                // left open overnight doesn't put yesterday's date on today's
                // sales (TICKET-01328).
                if (this.saleDateTopEnabled && newSale.saleDate) {
                    const saleDay = new Date(newSale.saleDate);
                    const now = new Date();
                    const isOtherDay = !Number.isNaN(saleDay.getTime())
                        && (saleDay.getFullYear() !== now.getFullYear()
                            || saleDay.getMonth() !== now.getMonth()
                            || saleDay.getDate() !== now.getDate());
                    this.updateKeyValue('lastSaleDate', isOtherDay ? newSale.saleDate : null);
                }
                // Only generate title if not already set (e.g., subscription products set their own title)
                if (!newSale.title) {
                    this.makeNewSaleTitle();
                }
                // Check if the sale is for production animals
                const { visitors, isProductionAnimal, user } = newSale;
                // Build the body once so we can also enqueue it verbatim if offline.
                const insertBody = {
                    ...newSale,
                    // Explicitly include user field to ensure it's sent (undefined values are excluded from JSON)
                    user: user || null,
                    isProductionAnimal,
                    calledFrom: props.calledFrom,
                };
                // Flags consumed by the body snapshot — an insert always carries the
                // full products array, and clearing here (instead of after the await)
                // keeps flags set by edits made during the round-trip (TICKET-00980).
                this.updateObjectKeyValue('newSale', 'productAdded', false);
                this.updateObjectKeyValue('newSale', 'productRemoved', false);
                let response = await this.insert(insertBody);

                // Offline detection: the network-error envelope means the POST
                // never reached the server. Enqueue and synthesize success so
                // the existing UI flow proceeds unchanged.
                if (isNetworkErrorEnvelope(response)) {
                    const tempId = await offlineWriteQueue.enqueueSale(insertBody, props.calledFrom || null);
                    if (tempId == null) {
                        // IndexedDB unavailable — be honest about the failure.
                        this.setSaveFeedback('error', response?.message || 'Failed to create sale');
                        return false;
                    }
                    response = {
                        status: 200,
                        data: { sale: { ...insertBody, id: tempId, _pendingSync: true } },
                    };
                    const queuedDisplay = {
                        ...insertBody,
                        id: tempId,
                        _pendingSync: true,
                    };
                    this.updateKeyValue('sales', [queuedDisplay, ...(this.sales || [])]);
                    this._setHasPendingSales((this.hasPendingSales || 0) + 1);
                    this.setSaveFeedback('success', 'Sale saved (will sync when online)');
                } else if (response?.status >= 400) {
                    this.setSaveFeedback('error', response?.message || 'Failed to create sale', response?.field);
                    return false;
                } else {
                    this.setSaveFeedback('success', 'Sale created');
                }

                const sale = response?.data?.sale;
                // Update current newSale object
                this.updateObjectKeyValue('newSale', 'id', sale?.id);
                this.updateObjectKeyValue('newSale', 'saleNumber', sale?.saleNumber);
                this.updateObjectKeyValue('newSale', 'updatedDate', sale?.updatedDate);
                this.updateObjectKeyValue('newSale', 'saleDate', sale?.saleDate);
                this.updateObjectKeyValue('newSale', 'user', sale?.user);
                this.updateObjectKeyValue('newSale', 'isProductionAnimal', this.isProductionAnimals);
            } finally {
                // Release the guard once the insert has resolved (newSale.id is
                // now set on success). Any caller parked on the promise above
                // resumes and re-runs saveSale as an update.
                this._newSaleInsertPromise = null;
                releaseInsertGuard();
            }
        }
        this.updateKeyValue('saleIsUpdated', false);
        // productAdded/productRemoved are consumed at snapshot time in each branch
        // above — clearing them here (after the awaits) wiped flags set by edits
        // made while a save was in flight, so the next debounced save went out
        // without its products payload (TICKET-00980).
        firebaseSync.broadcastSaleUpdate(newSale.id);
        return true;
    }

    async deleteSale(saleId, reason = '') {
        const response = await util.fetchApi(`/api/sales/${saleId}`, { method: 'DELETE' }, { reason });
        switch (response.status) {
            case 202:
                firebaseSync.broadcastSaleUpdate(saleId);
                return response;
            default:
                return response;
        }
    }

    /**
     * Creates a new sale with the given properties.
     *
     * @param {Object} props - The properties for the new sale.
     * @param {Array} [props.visitors=[]] - The visitors for the sale.
     * @param {Array} [props.animals=[]] - The animals for the sale.
     * @param {Object} props.user - The user for the sale.
     * @param {boolean} [props.skipSearchResults=false] - Whether to skip updating the search results.
     * @param {boolean} [props.skipVeterinaries=false] - Whether to skip updating the veterinaries.
     * @param {boolean} [props.skipVisitorResults=false] - Whether to skip updating the visitor results.
     * @param {boolean} [props.skipAnimalResults=false] - Whether to skip updating the animal results.
     * @param {boolean} [props.skipAnimalJournals=false] - Whether to skip updating the animal journals.
     * @param {boolean} [props.skipVisitorAnimals=false] - Whether to skip updating the visitor animals.
     * @param {boolean} [props.skipVisitorInvoices=false] - Whether to skip updating the visitor invoices.
     * @param {boolean} [props.skipProductResults=false] - Whether to skip updating the product results.
     * @param {boolean} [props.skipVisitorSales=false] - Whether to skip updating the visitor sales.
     * @param {boolean} [props.skipCurrentVisitorSales=false] - Whether to skip updating the current visitor sales.
     * @param {boolean} [props.skipCurrentPayments=false] - Whether to skip updating the current payments.
     */
    createNewSale(props = {}) {
        this.updateKeyValue('newSale', {
            id: null,
            visitors: props.visitors || [],
            animals: props.animals || [],
            journals: [],
            products: props.products || [],
            comments: [],
            user: props.user,
            productAdded: false,
            productRemoved: false,
            customer: props.customer,
            isWeborder: props.isWeborder,
            isProductionAnimal: this.isProductionAnimals,
            paymentMethod: props.paymentMethod,
            status: props.status,
            saleDate: this.saleDateTopEnabled
                ? (props.saleDate || this.lastSaleDate || util.isoDate(new Date(), { dateTimeSep: 'T' }))
                : props.saleDate,
            dueDate: props.dueDate,
        });
        if (!props.skipSearchResults) this.updateKeyValue('searchResults', []);
        if (!props.skipVeterinaries) this.updateKeyValue('veterinaries', []);
        // if (!props.skipEmployees) this.updateKeyValue('employees', []);
        if (!props.skipVisitorResults) this.updateKeyValue('visitorResults', []);
        if (!props.skipAnimalResults) this.updateKeyValue('animalResults', []);
        if (!props.skipAnimalJournals) this.updateKeyValue('animalJournals', []);
        if (!props.skipAnimalJournals) this.updateKeyValue('attestJournals', []);
        if (!props.skipVisitorAnimals) this.updateKeyValue('visitorAnimals', []);
        if (!props.skipVisitorInvoices) this.updateKeyValue('visitorInvoices', []);
        if (!props.skipProductResults) this.updateKeyValue('productResults', []);
        if (!props.skipVisitorSales) this.updateKeyValue('visitorSales', []);
        if (!props.skipCurrentVisitorSales) this.updateKeyValue('currentVisitorSales', []);
        if (!props.skipCurrentPayments) this.updateKeyValue('currentPayments', []);
        this.updateKeyValue('notUpdatedFields', []);
        this.updateKeyValue('calendarNotification', {});
        this.updateKeyValue('saleIsUpdated', false);
        this.getLastVisitors();
    }

    /**
     * Turn the sale on screen into a brand-new (unsaved) sale while keeping what
     * is already on it (product lines etc.). Used whenever the OWNER of the sale
     * on screen is replaced or removed: picking another visitor in the search,
     * removing the last owner pill, creating a new visitor from the sale page.
     *
     * Without this, a persisted sale kept its id through an owner swap and the
     * next save PATCHed the OLD sale in place — it silently became the other
     * owner's/animal's visit, with a stale title and the first animal's journal
     * orphaned (TICKET-01622). The visitor-search path had the guard inline;
     * this is the shared version.
     *
     * saleDate rule (TICKET-01328): with saleDateTop on, keep the date the user
     * already picked on an UNSAVED sale; a LOADED sale's date is not a user
     * pick, so fall back to the remembered backdate, else now. With saleDateTop
     * off, clear it (classic behaviour).
     *
     * @returns {boolean} true when the sale on screen was a persisted one.
     */
    @action
    detachFromPersistedSale() {
        const { newSale = {} } = this;
        const wasPersistedSale = !!newSale.id;
        this.updateObjectKeyValue('newSale', 'id', null);
        this.updateObjectKeyValue('newSale', 'saleNumber', null);
        if (this.saleDateTopEnabled) {
            const rememberedSaleDate = (!wasPersistedSale && newSale.saleDate)
                || this.lastSaleDate
                || util.isoDate(new Date(), { dateTimeSep: 'T' });
            this.updateObjectKeyValue('newSale', 'saleDate', rememberedSaleDate);
        } else {
            this.updateObjectKeyValue('newSale', 'saleDate', null);
        }
        return wasPersistedSale;
    }

    async getLastVisitors() {
        const response = await util.fetchApi(`/api/sales/visitors/last`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('lastVisitors', response.data);
                return response;
        }
    }

    async emailReceipt(sale, allCustomers = false, { to, subject, body } = {}) {
        // to/subject/body come from the "Send kvittering" drawer and override
        // the owner e-mail / default subject on the backend. Return the full
        // envelope so callers can show response.message instead of a false "sent".
        return util.fetchApi(`/api/sales/${sale.id}/email/receipt`, { method: 'GET' }, { allCustomers, to, subject, body });
    }

    async emailReminder({ sale, to, subject, body, includeJournal, purring }) {
        // Return the full envelope for every status: callers branch on
        // response.status and show response.message (e.g. the 403 from the
        // accounting-integration gate) instead of a false "sent".
        return util.fetchApi(`/api/sales/${sale.id}/email/reminder`, { method: 'GET' }, { to, subject, body, includeJournal, purring });
    }

    async friendlyReminder({ sale, to, subject, body }) {
        const response = await util.fetchApi(`/api/sales/${sale.id}/email/friendly-reminder`, { method: 'GET' }, { to, subject, body });
        switch (response.status) {
            case 200:
                return response;
            default:
                return 'Error';
        }
    }

    async smsReminder({ sale, message }) {
        const response = await util.fetchApi(`/api/sales/${sale.id}/sms/reminder`, { method: 'POST' }, { message });
        return response;
    }

    /**
     * "Skriv ut" bookkeeping: stamps printedDate/printedBy on the sale
     * (POST /api/sales/:id/printed) and mirrors it into newSale and the loaded
     * list, so the row / print buttons flip to "Skrevet ut" without a reload.
     * A printed invoice counts as sent by post in the "ikke sendt" filters and
     * the transferredNotSentToCustomer critical task (backend/lib/sale-printed.js).
     * Ongoing sales (status 0) are skipped: a draft printout must not hide the
     * invoice it later becomes.
     */
    async markPrinted(saleId) {
        if (!saleId) {
            return null;
        }
        const local = (this.newSale && this.newSale.id === saleId ? this.newSale : null)
            || (this.sales || []).find(s => s.id === saleId);
        if (local && !(local.status > 0)) {
            return null;
        }
        const response = await util.fetchApi(`/api/sales/${saleId}/printed`, { method: 'POST' }, {});
        if (response && response.status === 200) {
            const { printedDate, printedBy } = response.data || {};
            this.applyPrinted({ saleId, printedDate, printedBy });
        } else {
            console.error('saleStore.markPrinted', response);
        }
        return response;
    }

    @action
    applyPrinted({ saleId, printedDate, printedBy }) {
        if (this.newSale && this.newSale.id === saleId) {
            this.newSale.printedDate = printedDate;
            this.newSale.printedBy = printedBy;
        }
        const idx = (this.sales || []).findIndex(s => s.id === saleId);
        if (idx >= 0) {
            this.sales[idx].printedDate = printedDate;
            this.sales[idx].printedBy = printedBy;
        }
    }

    async emailPriceoffer({ sale, to, subject, body, includeJournal }) {
        const response = await util.fetchApi(`/api/sales/${sale.id}/email/priceoffer`, { method: 'GET' }, { to, subject, body, includeJournal });
        switch (response.status) {
            case 200:
                return response;
            default:
                return 'Error';
        }
    }

    async loadDailyReport(reportDate = '', options = {}) {
        this.updateKeyValue('reportStatus', null);
        this.updateKeyValue('cashCarryForward', null);
        const { requestVersion } = options;
        const response = await util.fetchApi(`/api/sales/report?reportDate=${reportDate}&reportDepartment=${this.reportDepartment || ''}`, { method: 'GET' }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            return { stale: true };
        }

        switch (response.status) {
            case 200:
                this.updateKeyValue('dailySales', response.data);
                this.updateKeyValue('cashCarryForward', response.included?.cashCarryForward || null);
                this.updateKeyValue('totalNumberOfSales', response.included?.totalNumberOfSales);
                this.updateKeyValue('aggSaleTotal', response.included?.saleTotal);
                this.updateKeyValue('aggSaleTotalCollectiveInvoice', response.included?.saleTotalCollectiveInvoice);
                this.updateKeyValue('aggSaleTotalDiscount', response.included?.saleTotalDiscount);
                this.updateKeyValue('aggSalesWithDiscount', response.included?.salesWithDiscount || []);
                this.updateKeyValue('aggSaleTotalNew', response.included?.saleTotalNew);
                this.updateKeyValue('aggSaleTotalPaymentMethods', response.included?.saleTotalPaymentMethods);
                this.updateKeyValue('aggSaleTotalPaymentMethodsNew', response.included?.saleTotalPaymentMethodsNew);
                this.updateKeyValue('aggSaleTotalTax', response.included?.saleTotalTax);
                this.updateKeyValue('aggSalePersons', response.included?.salePersons);
                this.updateKeyValue('aggSalePaymentsByUser', response.included?.salePaymentsByUser);
                this.updateKeyValue('aggSaleDepartments', response.included?.saleDepartments);
                this.updateKeyValue('aggSalePersonsAccounts', response.included?.salePersonsAccounts);
                this.updateKeyValue('aggSaleTotalAccounts', response.included?.saleTotalAccounts);
                this.updateKeyValue('aggSaleTotalProducts', response.included?.saleTotalProducts);
                this.updateKeyValue('aggSaleWithJournals', response.included?.salesWithJournals);
                this.updateKeyValue('aggSaleNewVisitors', response.included?.saleNewVisitors);
                this.updateKeyValue('aggSaleActiveVisitors', response.included?.saleActiveVisitors);
                this.updateKeyValue('journalCount', response.included?.journalCount);
                this.updateKeyValue('journalWithoutCount', response.included?.journalWithoutCount);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadKpiDashboard(startDate, endDate, department) {
        this.updateKeyValue('kpiDashboardLoading', true);
        this.updateKeyValue('kpiDashboardData', {});
        const params = new URLSearchParams({
            startDate: startDate || '',
            endDate: endDate || '',
        });
        if (department) {
            params.set('department', department);
        }
        const response = await util.fetchApi(`/api/sales/report/kpi-dashboard?${params.toString()}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('kpiDashboardData', response.included || {});
                break;
            default:
                break;
        }
        this.updateKeyValue('kpiDashboardLoading', false);
        return response;
    }

    async loadCriticalTasks() {
        this.updateKeyValue('criticalTasksLoading', true);
        const response = await util.fetchApi('/api/sales/report/critical-tasks', { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('criticalTasksData', response.included || {});
                break;
            default:
                break;
        }
        this.updateKeyValue('criticalTasksLoading', false);
        return response;
    }

    // The backend recomputes the alerts itself and bills one AI call on the user unless
    // the counts are unchanged (30-day cache). skipCache forces a fresh, billed answer.
    async loadCriticalTasksAdvice({ skipCache = false } = {}) {
        this.updateKeyValue('criticalTasksAdviceLoading', true);
        const response = await util.fetchApi('/api/ai/critical-tasks-advice', { method: 'POST' }, { skipCache });
        switch (response.status) {
            case 200:
                this.updateKeyValue('criticalTasksAdvice', { ...(response.data || {}), error: null });
                break;
            default:
                this.updateKeyValue('criticalTasksAdvice', {
                    ...this.criticalTasksAdvice,
                    error: response.message || 'Kunne ikke hente svar fra AI-assistenten.',
                });
                break;
        }
        this.updateKeyValue('criticalTasksAdviceLoading', false);
        return response;
    }

    async loadWeeklyChecklist() {
        this.updateKeyValue('weeklyChecklistLoading', true);
        const response = await util.fetchApi('/api/sales/report/weekly-checklist', { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('weeklyChecklist', response.data || {});
                break;
            default:
                // Mark the failure so the dashboard falls back to the daily card
                // instead of waiting forever for a week that never arrives.
                this.updateKeyValue('weeklyChecklist', { error: true, status: response.status });
                break;
        }
        this.updateKeyValue('weeklyChecklistLoading', false);
        return response;
    }

    async downloadKpiRawData(startDate, endDate, department) {
        const params = new URLSearchParams({
            startDate: startDate || '',
            endDate: endDate || '',
            includeRawData: 'true',
        });
        if (department) {
            params.set('department', department);
        }
        const response = await util.fetchApi(`/api/sales/report/kpi-dashboard?${params.toString()}`, { method: 'GET' }, {});
        if (response.status === 200 && response.included?.rawData) {
            return response.included.rawData;
        }
        return [];
    }

    async loadPeriodeReport(reportDateTimeStart = '', reportDateTimeEnd = '', options = {}) {
        this.updateKeyValue('reportStatus', null);
        const { requestVersion } = options;
        const response = await util.fetchApi(`/api/sales/report?reportDateTimeStart=${reportDateTimeStart}&reportDateTimeEnd=${reportDateTimeEnd}&reportDepartment=${this.reportDepartment || ''}`, { method: 'GET' }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            return { stale: true };
        }

        switch (response.status) {
            case 200:
                this.updateKeyValue('dailySales', response.data);
                this.updateKeyValue('totalNumberOfSales', response.included?.totalNumberOfSales);
                this.updateKeyValue('aggSaleTotal', response.included?.saleTotal);
                this.updateKeyValue('aggSaleTotalCollectiveInvoice', response.included?.saleTotalCollectiveInvoice);
                this.updateKeyValue('aggSaleTotalDiscount', response.included?.saleTotalDiscount);
                this.updateKeyValue('aggSalesWithDiscount', response.included?.salesWithDiscount || []);
                this.updateKeyValue('aggSaleTotalNew', response.included?.saleTotalNew);
                this.updateKeyValue('aggSaleTotalPaymentMethods', response.included?.saleTotalPaymentMethods);
                this.updateKeyValue('aggSaleTotalPaymentMethodsNew', response.included?.saleTotalPaymentMethodsNew);
                this.updateKeyValue('aggSaleTotalTax', response.included?.saleTotalTax);
                this.updateKeyValue('aggSalePersons', response.included?.salePersons);
                this.updateKeyValue('aggSalePaymentsByUser', response.included?.salePaymentsByUser);
                this.updateKeyValue('aggSaleDepartments', response.included?.saleDepartments);
                this.updateKeyValue('aggSalePersonsAccounts', response.included?.salePersonsAccounts);
                this.updateKeyValue('aggSaleTotalAccounts', response.included?.saleTotalAccounts);
                this.updateKeyValue('aggSaleTotalProducts', response.included?.saleTotalProducts);
                this.updateKeyValue('aggSaleWithJournals', response.included?.salesWithJournals);
                this.updateKeyValue('aggSaleNewVisitors', response.included?.saleNewVisitors);
                this.updateKeyValue('aggSaleActiveVisitors', response.included?.saleActiveVisitors);
                this.updateKeyValue('journalCount', response.included?.journalCount);
                this.updateKeyValue('journalWithoutCount', response.included?.journalWithoutCount);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadZReport(reportDate = '', options = {}) {
        this.updateKeyValue('reportStatus', null);
        const { requestVersion } = options;
        const response = await util.fetchApi(`/api/sales/report/z-report?reportDate=${reportDate}&reportDepartment=${this.reportDepartment || ''}`, { method: 'GET' }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            return { stale: true };
        }

        switch (response.status) {
            case 200:
                this.updateKeyValue('dailySales', response.data);
                this.updateKeyValue('totalNumberOfSales', response.included?.totalNumberOfSales);
                this.updateKeyValue('aggSaleTotal', response.included?.saleTotal);
                this.updateKeyValue('aggSaleTotalCollectiveInvoice', response.included?.saleTotalCollectiveInvoice);
                this.updateKeyValue('aggSaleTotalDiscount', response.included?.saleTotalDiscount);
                this.updateKeyValue('aggSalesWithDiscount', response.included?.salesWithDiscount || []);
                this.updateKeyValue('aggSaleTotalNew', response.included?.saleTotalNew);
                this.updateKeyValue('aggSaleTotalPaymentMethods', response.included?.saleTotalPaymentMethods);
                this.updateKeyValue('aggSaleTotalPaymentMethodsNew', response.included?.saleTotalPaymentMethodsNew);
                this.updateKeyValue('aggSaleTotalTax', response.included?.saleTotalTax);
                this.updateKeyValue('aggSalePersons', response.included?.salePersons);
                this.updateKeyValue('aggSalePaymentsByUser', response.included?.salePaymentsByUser);
                this.updateKeyValue('aggSaleDepartments', response.included?.saleDepartments);
                this.updateKeyValue('aggSalePersonsAccounts', response.included?.salePersonsAccounts);
                this.updateKeyValue('aggSaleTotalAccounts', response.included?.saleTotalAccounts);
                this.updateKeyValue('aggSaleTotalProducts', response.included?.saleTotalProducts);
                this.updateKeyValue('aggSaleWithJournals', response.included?.salesWithJournals);
                this.updateKeyValue('aggSaleNewVisitors', response.included?.saleNewVisitors);
                this.updateKeyValue('aggSaleActiveVisitors', response.included?.saleActiveVisitors);
                this.updateKeyValue('journalCount', response.included?.journalCount);
                this.updateKeyValue('journalWithoutCount', response.included?.journalWithoutCount);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadMonthlyReport(reportDate = new Date(), onlyFromDate = false, options = {}) {
        this.updateKeyValue('reportStatus', null);
        const { requestVersion } = options;
        const date = new Date(reportDate);
        let startOfMonth;
        if (onlyFromDate) {
            startOfMonth = util.isoDate(date, 1);
        } else {
            startOfMonth = util.isoDate(new Date(date.getFullYear(), date.getMonth(), 1));
        }
        const endOfMonth = util.isoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
        const response = await util.fetchApi(`/api/sales/report/month?startDate=${startOfMonth}&endDate=${endOfMonth}&reportDepartment=${this.reportDepartment || ''}`, { method: 'GET' }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            return { stale: true };
        }

        switch (response.status) {
            case 200:
                this.updateKeyValue('dailySales', response.data);
                this.updateKeyValue('totalNumberOfSales', response.included?.totalNumberOfSales);
                this.updateKeyValue('aggSaleTotal', response.included?.saleTotal);
                this.updateKeyValue('aggSaleTotalCollectiveInvoice', response.included?.saleTotalCollectiveInvoice);
                this.updateKeyValue('aggSaleTotalDiscount', response.included?.saleTotalDiscount);
                this.updateKeyValue('aggSalesWithDiscount', response.included?.salesWithDiscount || []);
                this.updateKeyValue('aggSaleTotalNew', response.included?.saleTotalNew);
                this.updateKeyValue('aggSaleTotalPaymentMethods', response.included?.saleTotalPaymentMethods);
                this.updateKeyValue('aggSaleTotalPaymentMethodsNew', response.included?.saleTotalPaymentMethodsNew);
                this.updateKeyValue('aggSaleTotalTax', response.included?.saleTotalTax);
                this.updateKeyValue('aggSalePersons', response.included?.salePersons);
                this.updateKeyValue('aggSalePaymentsByUser', response.included?.salePaymentsByUser);
                this.updateKeyValue('aggSaleDepartments', response.included?.saleDepartments);
                this.updateKeyValue('aggSalePersonsAccounts', response.included?.salePersonsAccounts);
                this.updateKeyValue('aggSaleTotalAccounts', response.included?.saleTotalAccounts);
                this.updateKeyValue('aggSaleTotalProducts', response.included?.saleTotalProducts);
                this.updateKeyValue('aggSaleWithJournals', response.included?.salesWithJournals);
                this.updateKeyValue('aggSaleNewVisitors', response.included?.saleNewVisitors);
                this.updateKeyValue('aggSaleActiveVisitors', response.included?.saleActiveVisitors);
                this.updateKeyValue('journalCount', response.included?.journalCount);
                this.updateKeyValue('journalWithoutCount', response.included?.journalWithoutCount);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');

        }
    }

    async loadYearlyReport(reportDate = new Date(), onlyFromDate = false, options = {}) {
        this.updateKeyValue('reportStatus', null);
        const { requestVersion } = options;
        const date = new Date(reportDate);
        let startOfYear;
        if (onlyFromDate) {
            startOfYear = util.isoDate(date, 1);
        } else {
            startOfYear = util.isoDate(new Date(date.getFullYear(), 0, 1));
        }
        const endOfYear = util.isoDate(new Date(date.getFullYear(), 12, 0));
        const response = await util.fetchApi(`/api/sales/report/year?startDate=${startOfYear}&endDate=${endOfYear}&reportDepartment=${this.reportDepartment || ''}`, { method: 'GET' }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            return { stale: true };
        }

        switch (response.status) {
            case 200:
                this.updateKeyValue('dailySales', response.data);
                this.updateKeyValue('totalNumberOfSales', response.included?.totalNumberOfSales);
                this.updateKeyValue('aggSaleTotal', response.included?.saleTotal);
                this.updateKeyValue('aggSaleTotalCollectiveInvoice', response.included?.saleTotalCollectiveInvoice);
                this.updateKeyValue('aggSaleTotalDiscount', response.included?.saleTotalDiscount);
                this.updateKeyValue('aggSalesWithDiscount', response.included?.salesWithDiscount || []);
                this.updateKeyValue('aggSaleTotalNew', response.included?.saleTotalNew);
                this.updateKeyValue('aggSaleTotalPaymentMethods', response.included?.saleTotalPaymentMethods);
                this.updateKeyValue('aggSaleTotalPaymentMethodsNew', response.included?.saleTotalPaymentMethodsNew);
                this.updateKeyValue('aggSaleTotalTax', response.included?.saleTotalTax);
                this.updateKeyValue('aggSalePersons', response.included?.salePersons);
                this.updateKeyValue('aggSalePaymentsByUser', response.included?.salePaymentsByUser);
                this.updateKeyValue('aggSaleDepartments', response.included?.saleDepartments);
                this.updateKeyValue('aggSalePersonsAccounts', response.included?.salePersonsAccounts);
                this.updateKeyValue('aggSaleTotalAccounts', response.included?.saleTotalAccounts);
                this.updateKeyValue('aggSaleTotalProducts', response.included?.saleTotalProducts);
                this.updateKeyValue('aggSaleWithJournals', response.included?.salesWithJournals);
                this.updateKeyValue('aggSaleNewVisitors', response.included?.saleNewVisitors);
                this.updateKeyValue('aggSaleActiveVisitors', response.included?.saleActiveVisitors);
                this.updateKeyValue('journalCount', response.included?.journalCount);
                this.updateKeyValue('journalWithoutCount', response.included?.journalWithoutCount);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadReportUserDetails(userId, reportDate, type, onlyFromDate, datefrom, dateto) {
        this.updateKeyValue('reportUserDetails', null);
        const response = await util.fetchApi(`/api/sales/report/user-detail?&reportDate=${reportDate}&type=${type}&onlyFromDate=${onlyFromDate}&userId=${userId}&startDate=${datefrom}&endDate=${dateto}&reportDepartment=${this.reportDepartment || ''}`, {
            method: 'GET',
        }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('reportUserDetails', response.data);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadReportUserDetailsProducts(userId, reportDate, type, onlyFromDate, datefrom, dateto) {
        this.updateKeyValue('reportUserDetails', null);
        const response = await util.fetchApi(`/api/sales/report/user-detail-products?&reportDate=${reportDate}&type=${type}&onlyFromDate=${onlyFromDate}&userId=${userId}&startDate=${datefrom}&endDate=${dateto}&reportDepartment=${this.reportDepartment || ''}`, {
            method: 'GET',
        }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('reportUserDetails', response.data);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadReportAllSales(reportDate, type, onlyFromDate, datefrom, dateto, paymentMethod = '') {
        this.updateKeyValue('reportAllSales', null);
        const response = await util.fetchApi(`/api/sales/report/all?&reportDate=${reportDate}&type=${type}&onlyFromDate=${onlyFromDate}&startDate=${datefrom}&endDate=${dateto}&reportDepartment=${this.reportDepartment || ''}&paymentMethod=${paymentMethod || ''}`, {
            method: 'GET',
        }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('reportAllSales', response.data);
                return response;
            case 500:
                this.updateKeyValue('reportStatus', 'Error loading reports.');
            default:
                this.updateKeyValue('reportStatus', 'Something went wrong. Please try again.');
        }
    }

    async loadCashflowAnalysis(startDate, endDate) {
        this.updateKeyValue('cashflowAnalysisLoading', true);
        this.updateKeyValue('cashflowAnalysis', null);
        const response = await util.fetchApi(`/api/sales/report/cashflow-analysis?startDate=${startDate}&endDate=${endDate}&department=${this.reportDepartment || ''}`, {
            method: 'GET',
        }, {});
        this.updateKeyValue('cashflowAnalysisLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('cashflowAnalysis', response.data);
                return response.data;
            default:
                this.updateKeyValue('cashflowAnalysis', null);
                return null;
        }
    }

    async loadCardBreakdown(startDate, endDate) {
        this.updateKeyValue('cardBreakdownLoading', true);
        this.updateKeyValue('cardBreakdown', null);
        const response = await util.fetchApi(`/api/sales/report/card-breakdown?startDate=${startDate}&endDate=${endDate}&department=${this.reportDepartment || ''}`, {
            method: 'GET',
        }, {});
        this.updateKeyValue('cardBreakdownLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('cardBreakdown', response.data);
                return response.data;
            default:
                this.updateKeyValue('cardBreakdown', null);
                return null;
        }
    }

    async loadWriteoffs(startDate, endDate) {
        this.updateKeyValue('writeoffsLoading', true);
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        const response = await util.fetchApi(`/api/sales/writeoffs?${params.toString()}`, {
            method: 'GET',
        }, {});
        this.updateKeyValue('writeoffsLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('writeoffs', response.data || []);
                this.updateKeyValue('writeoffsSummary', response.summary || {});
                return response.data;
            default:
                this.updateKeyValue('writeoffs', []);
                this.updateKeyValue('writeoffsSummary', {});
                return [];
        }
    }

    async loadYearlyAccountsReport(year, accountFilter = '3', startMonth = 1, endMonth = 12, options = {}) {
        this.updateKeyValue('yearlyAccountsReportLoading', true);
        this.updateKeyValue('yearlyAccountsReport', null);
        const { requestVersion } = options;
        const params = new URLSearchParams();
        params.append('year', year);
        if (accountFilter) params.append('accountFilter', accountFilter);
        if (startMonth) params.append('startMonth', startMonth);
        if (endMonth) params.append('endMonth', endMonth);
        if (this.reportDepartment) params.append('reportDepartment', this.reportDepartment);
        const response = await util.fetchApi(`/api/sales/report/accounts-yearly?${params.toString()}`, {
            method: 'GET',
        }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            this.updateKeyValue('yearlyAccountsReportLoading', false);
            return { stale: true };
        }

        this.updateKeyValue('yearlyAccountsReportLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('yearlyAccountsReport', response.data);
                return response.data;
            default:
                this.updateKeyValue('yearlyAccountsReport', null);
                return null;
        }
    }

    async loadAccountsReceivableReport(cutoffDate, options = {}) {
        this.updateKeyValue('accountsReceivableReportLoading', true);
        this.updateKeyValue('accountsReceivableReport', null);
        const { requestVersion, startDate } = options;
        const params = new URLSearchParams();
        if (cutoffDate) params.append('cutoffDate', cutoffDate);
        if (startDate) params.append('startDate', startDate);
        if (this.reportDepartment) params.append('reportDepartment', this.reportDepartment);
        const response = await util.fetchApi(`/api/sales/report/accounts-receivable?${params.toString()}`, {
            method: 'GET',
        }, {});

        // Check if this request is still valid before updating store
        if (requestVersion !== undefined && requestVersion !== this.reportRequestVersion) {
            this.updateKeyValue('accountsReceivableReportLoading', false);
            return { stale: true };
        }

        this.updateKeyValue('accountsReceivableReportLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('accountsReceivableReport', response.data);
                return response.data;
            default:
                this.updateKeyValue('accountsReceivableReport', null);
                return null;
        }
    }

    async loadCustomerLedger(visitorId, options = {}) {
        const { allSales = false } = options;
        this.updateKeyValue('customerLedgerLoading', true);
        this.updateKeyValue('customerLedger', null);
        const params = new URLSearchParams();
        if (Array.isArray(visitorId)) {
            visitorId.forEach(id => params.append('visitor', id));
        } else {
            params.append('visitor', visitorId);
        }
        if (allSales) {
            params.append('allSales', '1');
        }
        const response = await util.fetchApi(`/api/sales/customer-ledger?${params.toString()}`, { method: 'GET' }, {});
        this.updateKeyValue('customerLedgerLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('customerLedger', response.data);
                return response.data;
            default:
                this.updateKeyValue('customerLedger', null);
                return null;
        }
    }

    async loadSaleTrends(months = 12) {
        const response = await util.fetchApi(`/api/sales/trends?months=${months}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('saleTrends', response.data);
                return response.data;
            default:
                this.updateKeyValue('saleTrends', []);
                return [];
        }
    }

    async loadVisitorTrends(months = 12) {
        const response = await util.fetchApi(`/api/sales/visitor-trends?months=${months}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorTrends', response.data);
                return response.data;
            default:
                this.updateKeyValue('visitorTrends', []);
                return [];
        }
    }

    async markAsPaid(object) {
        console.log('[SaleStore] markAsPaid called with:', object);
        const response = await util.fetchApi(`/api/sales/mark-as-paid/${object.id}`, {
            method: 'PATCH',
        }, { ...object });
        console.log('[SaleStore] markAsPaid response:', response);
        switch (response.status) {
            case 202:
                return response;
            case 500:
                console.error('[SaleStore] markAsPaid returned 500');
                return 'Error';
            default:
                console.error('[SaleStore] markAsPaid unexpected status:', response.status);
                return 'Error';
        }
    }

    async setPaymentMethod(object) {
        const response = await util.fetchApi(`/api/sales/set-payment-method/${object.id}`, {
            method: 'PATCH',
        }, { ...object });
        switch (response.status) {
            case 202:
                return response;
            case 500:
                return 'Error';
            default:
                return 'Error';
        }
    }

    // NETS
    async netsCreateTransaction({ saleId, netsTerminalId, amount }) {
        const response = await util.fetchApi(`/api/integrations/nets/create-txn/${netsTerminalId}`, { method: 'POST' }, {
            saleId,
            amount,
        });
        switch (response.status) {
            case 201:
                return true;
            case 500:
                return false;
            default:
                return false;
        }
    }
    // /NETS

    getPaymentMethodTotal(method) {
        const { aggSaleTotalPaymentMethods } = this;
        const paymentMethod = aggSaleTotalPaymentMethods.find(item => item.paymentMethod === method);
        if (paymentMethod) return paymentMethod.price;// * (1 + paymentMethod.vat / 100);
        return 0;
    }

    getPaymentMethodTotalNew(method) {
        const { aggSaleTotalPaymentMethodsNew } = this;
        const paymentMethod = aggSaleTotalPaymentMethodsNew.find(item => item.paymentMethod === method);
        if (paymentMethod) return paymentMethod.price;// * (1 + paymentMethod.vat / 100);
        return 0;
    }

    checkForUpdates(clearCounter = true) {
        if (clearCounter) {
            this.checkForUpdatesCounter = 0;
        }
        const { newSale } = this;
        // console.log('checkForUpdates', newSale.id);
        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
            this.doCheckForUpdates();
        }, 60000);
    }

    async doCheckForUpdates() {
        const { newSale } = this;
        if (this.checkForUpdatesCounter > 60) {
            return false;
        }
        if (newSale.id) {
            const response = await this.load({
                query: {
                    id: newSale.id,
                },
                skipUpdate: true,
            });
            const lastVersion = response[0];
            if (lastVersion.updatedDate > newSale.updatedDate) {
                this.updateKeyValue('saleIsUpdated', true);
                // console.log('---> sale has been updated!');
                // console.log('newSale', newSale.updatedDate);
                // console.log('lastVersion', lastVersion.updatedDate);
            } else {
                this.updateKeyValue('saleIsUpdated', false);
            }
            this.checkForUpdatesCounter += 1;
        } else {
            this.checkForUpdatesCounter = 0;
        }
        this.checkForUpdates(false);
    }

    async duplicateSale(saleId) {
        const response = await util.fetchApi(`/api/sales/duplicate/${saleId}`, { method: 'POST' }, {});
        switch (response.status) {
            case 201:
                this.updateKeyValue('saleDuplicate', response.data?.sale);
                return response?.data?.sale;
            case 500:
                return 'Error';
            default:
                return 'Error';
        }
    }

    /**
     * Send a saved sale to Convene (Order/Import). The backend moves the sale to
     * status 9 (Sendt til Convene) on success and stores the Convene error on the
     * sale otherwise. Returns the raw API response; route it through
     * lib/conveneErrorMessage to show the vet what went wrong (T-01609).
     * Used by the samlefaktura flows (TICKET-01715); the payment drawer has its own
     * call because it also mirrors the Convene ids into newSale.
     */
    async sendSaleToConvene(saleId) {
        return util.fetchApi(`/api/integrations/convene/orders/${saleId}`, { method: 'POST' }, {});
    }

    @action
    async mergeSale({ primaryId, secondaryId }) {
        const response = await util.fetchApi('/api/sales/sale-merge', { method: 'POST' }, {
            primaryId,
            secondaryId,
        });
        return response;
    }

    /**
     * Creates a quick sale for direct payment flow (bypasses cart/checkout).
     * Used for race subscription purchases with Vipps/PayPal.
     *
     * @param {Object} options - The options for creating the sale.
     * @param {Object} options.product - The product to purchase.
     * @param {Object} options.user - The user making the purchase.
     * @param {number} options.customer - The customer ID.
     * @param {string} options.paymentMethod - The payment method ('vippsIntegration' or 'paypal').
     * @param {number} options.race - Optional race ID for subscription products.
     * @returns {Object} The created sale object.
     */
    async createQuickSale({ product, user, customer, paymentMethod, race }) {
        // Look up the visitor record for this user
        // Visitor is different from user - visitor is the customer record for sales
        // The endpoint uses JWT to get currentUser, then finds/creates visitor by cellphone or email
        const visitorResponse = await util.fetchApi('/api/visitors/loadFromUser', { method: 'GET' }, {});
        const visitor = visitorResponse?.data?.visitor;

        if (!visitor || !visitor.id) {
            console.error('[SaleStore] createQuickSale: No visitor found for user', user.id);
            return null;
        }

        // Build product line with all necessary fields
        const productLine = {
            id: product.id,
            name: product.name,
            price: product.price,
            vat: product.vat || 0,
            qty: 1,
            isGlobalSubscription: product.isGlobalSubscription,
            race: product.race || race,
            customer: product.customer,
            afterPurchaseUrl: product.afterPurchaseUrl,
            afterPurchaseButtonText: product.afterPurchaseButtonText,
        };

        // Build sale title from visitor name
        const saleTitle = `${visitor.firstname || ''} ${visitor.lastname || ''} - ${product.name}`.trim();

        // Create sale via API with the looked-up visitor
        const response = await util.fetchApi('/api/sales/', { method: 'POST' }, {
            title: saleTitle,
            user: user.id,
            customer: customer,
            isWeborder: true,
            paymentMethod: paymentMethod,
            visitors: [visitor],
            products: [productLine],
        });

        if (response?.status === 201 || response?.status === 200) {
            return response?.data?.sale;
        }

        console.error('[SaleStore] createQuickSale failed:', response);
        return null;
    }

    @action
    async bulkMarkCollectiveInvoice({ cutoffDate, cutoffDateEnd }) {
        const response = await util.fetchApi('/api/sales/bulk-mark-collective-invoice', { method: 'PATCH' }, {
            cutoffDate,
            cutoffDateEnd,
        });
        return response;
    }

    @action
    async loadChanges({ query = {}, offset = 0, limit = 100, addData = [] }) {
        const params = new URLSearchParams({
            limit,
            offset,
            ...query,
        });

        const url = `${this.api.changes.url}?${params}`;
        const response = await util.fetchApi(url, { method: 'GET' }, {});

        switch (response.status) {
            case 200:
                this.updateKeyValue('sales', response.data || []);
                this.updateKeyValue('totalSales', response.included?.totalSales || 0);
                this.updateKeyValue('changeField', response.included?.field || null);
                if (response.included?.users) {
                    this.updateKeyValue('saleUsers', response.included.users);
                }
                return response.data;
            default:
                console.error('Error loading sales with changes:', response);
                throw new Error('Failed to load sales with changes');
        }
    }
}

const store = new SaleStore();
export default store;
